/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useForm, Controller, FieldError } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { OrderItemData } from '../models/order';
import Button from '../components/atoms/Button';
import SectionTitle from '../components/atoms/SectionTitle';
import { PollData, QuestionDTO, QuestionResponse } from '../models/poll';
import { setPollData } from '../store';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';
import Input from '../components/atoms/Input';
import CustomAutocomplete from '../components/atoms/Autocomplete';
import EventDTO from '../models/event';

interface QuestionsFormProps {
  questions: QuestionDTO[];
  orderItems: OrderItemData[];
  event: EventDTO;
}

type SchemaType = {
  [key: string]: yup.StringSchema;
};

const validationSchema = (
  questions: QuestionDTO[],
  orderItems: OrderItemData[],
) => {
  const schema: SchemaType = {};

  orderItems.forEach((item, index) => {
    questions.forEach((question) => {
      const key = `${index}_${question.id}`;
      if (question.mandatory) {
        schema[key] = yup.string().required(`${question.value} es obligatorio`);
      } else {
        schema[key] = yup.string();
      }
    });
  });

  return yup.object().shape(schema);
};

const getErrorMessage = (error: FieldError | undefined) => error?.message;

const QuestionsForm: React.FC<QuestionsFormProps> = ({
  questions,
  orderItems,
  event,
}) => {
  const navigate = useNavigateWithQuery();
  const dispatch = useDispatch();
  const [sortedQuestions, setSortedQuestions] = useState<QuestionDTO[]>([]);

  useEffect(() => {
    if (questions) {
      setSortedQuestions([...questions].sort((a, b) => a.order - b.order));
    }
  }, [questions]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(validationSchema(questions, orderItems)),
    mode: 'onChange',
  });

  const onSubmit = (data: any) => {
    const mappedData: PollData[] = orderItems.map((item, index) => {
      const responses: QuestionResponse[] = questions.map((q) => ({
        question_id: q.id,
        question_text: q.value,
        value: data[`${index}_${q.id}`],
      }));
      return {
        name: item.name,
        last_name: item.last_name,
        person_id: item.person_id,
        question_responses: responses,
      };
    });
    dispatch(setPollData(mappedData));
    navigate('../checkout?customized=true');
  };

  const onGoBack = () => {
    localStorage.removeItem('formItemsData');
    navigate('../entry-data');
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full h-full">
        <div className='glass-surface flex flex-col gap-4 p-3'>
          {orderItems.map((item, index) => (
            <div key={index}>
              <SectionTitle
                index={index}
                customizeData={true}
                item={item.ticket}
                event={event}
              />
              <div className="flex flex-col gap-4">
                <Input
                  id={`fullname_${index}`}
                  label="Nombre y Apellido*"
                  type="text"
                  value={`${item.name} ${item.last_name}`}
                  disabled
                  labelAbove
                />
                {sortedQuestions.map((question, qIndex) => (
                  <div key={qIndex}>
                    {question.is_open ? (
                      <Controller
                        name={`${index}_${question.id}`}
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <Input
                            {...field}
                            id={`${index}_${question.id}`}
                            label={`${question.value}${question.mandatory ? '*' : ''}`}
                            labelAbove
                            type={question.value === 'Edad' ? 'number' : 'text'}
                            placeholder={question.value}
                            inputMode={question.value === 'Edad' ? 'numeric' : 'text'}
                            pattern={question.value === 'Edad' ? '[0-9]*' : undefined}
                            min={question.value === 'Edad' ? 1 : undefined}
                            max={question.value === 'Edad' ? 99 : undefined}
                            errMsg={getErrorMessage(errors[`${index}_${question.id}`] as FieldError)}
                          />
                        )}
                      />
                    ) : (
                      <CustomAutocomplete
                        id={`${index}_${question.id}`}
                        name={`${index}_${question.id}`}
                        control={control}
                        label={`${question.value}${question.mandatory ? '*' : ''}`}
                        options={question.options || []}
                        freeSolo={false}
                        labelAbove
                        errMsg={getErrorMessage(errors[`${index}_${question.id}`] as FieldError)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col-reverse md:flex-row justify-center gap-8 mt-10">
          <Button type="button" variant="secondary" onClick={onGoBack}>
            Volver
          </Button>
          <Button type="submit" className="mb-2 h-12" variant="primary">
            Siguiente
          </Button>
        </div>
      </form>
    </div>
  );
};

export default QuestionsForm;
